// Section 17 and 22 priority order editor: drag a group to rank it, or focus it and press the
// arrow keys (Up and Down move it one place; Home and End to the top or bottom). Props only.
import { useEffect, useRef, useState } from 'preact/hooks';
import type { PriorityGroupId } from '../../sim/api';
import { dropGroup, moveGroup, type GroupRow } from '../../viewModels/rations';
import { priorityGroupLabel, rationsText } from '../text/en';

interface Props {
  order: GroupRow[];
  unused: GroupRow[];
  canEdit: boolean;
  onReorder: (groups: PriorityGroupId[]) => void;
}

export function PriorityEditor({ order, unused, canEdit, onReorder }: Props) {
  const ids = order.map((g) => g.id);
  const [focusId, setFocusId] = useState<PriorityGroupId | null>(null);
  const [dragging, setDragging] = useState<PriorityGroupId | null>(null);
  const [message, setMessage] = useState('');
  const [adding, setAdding] = useState<PriorityGroupId | ''>('');
  const items = useRef(new Map<PriorityGroupId, HTMLLIElement>());

  // Keep focus on the group that just moved (it re-renders at its new place).
  useEffect(() => {
    if (focusId !== null) items.current.get(focusId)?.focus();
  }, [order, focusId]);

  const reorder = (next: PriorityGroupId[], moved: PriorityGroupId): void => {
    if (next.join() === ids.join()) return;
    onReorder(next);
    setFocusId(moved);
    setMessage(rationsText.orderMoved(priorityGroupLabel[moved], next.indexOf(moved) + 1));
  };

  const onKey = (id: PriorityGroupId, e: KeyboardEvent): void => {
    if (!canEdit || e.target !== e.currentTarget) return;
    const moves: Partial<Record<string, () => PriorityGroupId[]>> = {
      ArrowUp: () => moveGroup(ids, id, -1),
      ArrowDown: () => moveGroup(ids, id, 1),
      Home: () => dropGroup(ids, id, 0),
      End: () => dropGroup(ids, id, ids.length),
    };
    const move = moves[e.key];
    if (move === undefined) return;
    e.preventDefault();
    reorder(move(), id);
  };

  return (
    <div class="rations__editor">
      <p class="hint" id="rations-order-hint">
        {rationsText.orderHint}
      </p>
      <ol class="rations__order" aria-label={rationsText.orderHeading}>
        {order.map((g, index) => {
          const label = priorityGroupLabel[g.id];
          return (
            <li
              key={g.id}
              ref={(el) => {
                if (el === null) items.current.delete(g.id);
                else items.current.set(g.id, el);
              }}
              class={`rations__group${dragging === g.id ? ' is-dragging' : ''}`}
              tabIndex={0}
              aria-describedby="rations-order-hint"
              draggable={canEdit}
              onKeyDown={(e) => {
                onKey(g.id, e);
              }}
              onDragStart={(e) => {
                setDragging(g.id);
                e.dataTransfer?.setData('text/plain', g.id);
              }}
              onDragEnd={() => {
                setDragging(null);
              }}
              onDragOver={(e) => {
                if (canEdit) e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const moved = (e.dataTransfer?.getData('text/plain') ??
                  dragging) as PriorityGroupId;
                setDragging(null);
                if (ids.includes(moved)) reorder(dropGroup(ids, moved, index), moved);
              }}
            >
              <span class="rations__grip" aria-hidden="true" title={rationsText.grip}>
                ⋮⋮
              </span>
              <span class="rations__rank num">{index + 1}</span>
              <span class="rations__label">{label}</span>
              <span class="rations__count hint">{rationsText.members(g.members)}</span>
              <span class="rations__moves">
                <button
                  type="button"
                  class="button button--small"
                  aria-label={rationsText.moveUp(label)}
                  disabled={!canEdit || index === 0}
                  onClick={() => {
                    reorder(moveGroup(ids, g.id, -1), g.id);
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  class="button button--small"
                  aria-label={rationsText.moveDown(label)}
                  disabled={!canEdit || index === order.length - 1}
                  onClick={() => {
                    reorder(moveGroup(ids, g.id, 1), g.id);
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  class="button button--small"
                  aria-label={rationsText.remove(label)}
                  disabled={!canEdit}
                  onClick={() => {
                    onReorder(ids.filter((x) => x !== g.id));
                  }}
                >
                  ×
                </button>
              </span>
            </li>
          );
        })}
      </ol>
      <p class="visually-hidden" aria-live="polite">
        {message}
      </p>
      {unused.length > 0 && (
        <div class="rations__add">
          <label class="field">
            <span class="field__label">{rationsText.addGroup}</span>
            <select
              value={adding}
              disabled={!canEdit}
              onChange={(e) => {
                setAdding(e.currentTarget.value as PriorityGroupId | '');
              }}
            >
              <option value="">{rationsText.addGroupChoose}</option>
              {unused.map((g) => (
                <option key={g.id} value={g.id}>
                  {priorityGroupLabel[g.id]} ({rationsText.members(g.members)})
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            class="button"
            disabled={!canEdit || adding === ''}
            onClick={() => {
              if (adding === '') return;
              onReorder([...ids, adding]);
              setAdding('');
            }}
          >
            {rationsText.add}
          </button>
        </div>
      )}
    </div>
  );
}
